import type { WsRole } from '@fundline/shared/auth/claims';
import { InviteResponseSchema, type InviteRequest } from '@fundline/shared/schemas/members';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { workerApi } from '../../lib/worker-api.ts';

export type MemberStatus = 'invited' | 'active' | 'disabled';

export interface Member {
  membershipId: string;
  userId: string;
  role: WsRole;
  status: MemberStatus;
  createdAt: string;
  fullName: string;
  email: string;
}

export async function fetchMembers(workspaceId: string): Promise<Member[]> {
  const { data, error } = await supabase()
    .from('memberships')
    .select(
      'id, user_id, role, status, created_at, profile:profiles!memberships_user_id_fkey(full_name, email)',
    )
    .eq('workspace_id', workspaceId)
    .order('created_at');
  if (error) throw new Error(`Couldn't load members: ${error.message}`);
  return data.map((row) => ({
    membershipId: row.id,
    userId: row.user_id,
    role: row.role,
    status: row.status,
    createdAt: row.created_at,
    fullName: row.profile?.full_name ?? '',
    email: row.profile?.email ?? '',
  }));
}

export function useMembersQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.members(workspaceId),
    queryFn: () => fetchMembers(workspaceId),
  });
}

/** Role or status change by an admin (RLS + column grants + triggers enforce the rules). */
export function useUpdateMember(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      membershipId: string;
      patch: { role: WsRole } | { status: 'active' | 'disabled' };
    }) => {
      const { data, error } = await supabase()
        .from('memberships')
        .update(input.patch)
        .eq('workspace_id', workspaceId)
        .eq('id', input.membershipId)
        .select('id');
      if (error) throw new Error(error.message);
      if (data.length === 0) throw new Error('You are not allowed to change this member.');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.members(workspaceId) }),
  });
}

export function useInvite(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InviteRequest) =>
      workerApi(`/workspaces/${workspaceId}/invites`, {
        method: 'POST',
        body,
        schema: InviteResponseSchema,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.members(workspaceId) }),
  });
}

export function useReissueLink(workspaceId: string) {
  return useMutation({
    mutationFn: (userId: string) =>
      workerApi(`/workspaces/${workspaceId}/members/${userId}/invite-link`, {
        method: 'POST',
        body: {},
        schema: InviteResponseSchema,
      }),
  });
}
